// client/admin-client/src/routes/ProtectedRoutes.js
import React from 'react';
import { fetchMyLoungePermissions } from '../api/adminPermissionApi';
import { Navigate } from 'react-router-dom';
import {
  clearAdminSession,
  getAdminToken,
  getStoredAdminUser,
  isAdminTokenValid,
} from '../auth/adminSession';

function getUser() {
  return getStoredAdminUser();
}

function hasValidToken() {
  const token = getAdminToken();
  if (!token) return false;
  if (isAdminTokenValid(token)) return true;

  // 만료/손상된 토큰과 사용자 캐시를 즉시 제거한다.
  clearAdminSession();
  return false;
}

export function getCurrentUserLevel() {
  return String(getUser()?.user_level || '').trim().toLowerCase();
}

export function AuthRoute({ children }) {
  if (!hasValidToken()) return <Navigate to="/login" replace />;
  return children;
}

// allow 배열에 포함된 lv만 허용
export function LevelRoute({ children, allow = [] }) {
  if (!hasValidToken()) return <Navigate to="/login" replace />;

  const currentLevel = getCurrentUserLevel();

  if (Array.isArray(allow) && allow.length > 0 && !allow.includes(currentLevel)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}

export function isSuperAdminUser(user = getUser()) {
  // 최고관리자 UI 판정도 서버와 동일하게 고정 user_code만 기준으로 사용한다.
  return String(user?.user_code || '') === '10000000';
}

export function SuperAdminRoute({ children }) {
  if (!hasValidToken()) return <Navigate to="/login" replace />;
  if (!isSuperAdminUser()) return <Navigate to="/unauthorized" replace />;
  return children;
}

// 기존 호환용
export function AdminRoute({ children }) {
  return <LevelRoute allow={['lv1']}>{children}</LevelRoute>;
}

// Front-end convenience gate; backend independently authorizes every lounge API.
export function LoungePermissionRoute({ children }) {
  const [status, setStatus] = React.useState('loading');
  const token = getAdminToken();
  React.useEffect(() => {
    if (!hasValidToken()) { setStatus('unauthenticated'); return undefined; }
    const controller = new AbortController();
    fetchMyLoungePermissions(controller.signal).then((data) => {
      if (!controller.signal.aborted) setStatus(data.permissions?.view === true ? 'allowed' : 'denied');
    }).catch(() => { if (!controller.signal.aborted) setStatus('denied'); });
    return () => controller.abort();
  }, [token]);
  if (!hasValidToken() || status === 'unauthenticated') return <Navigate to="/login" replace />;
  if (status === 'loading') return <div className="p-8 text-center text-gray-400">라운지 권한 확인 중...</div>;
  if (status !== 'allowed') return <Navigate to="/unauthorized" replace />;
  return children;
}

// 메뉴 접근 자체는 LV1~LV4 기본 제공. 서버 API는 인증된 AgentNode를 검증하고
// LV2~LV4에 대해서만 자신 + 하위 조직 데이터로 범위를 제한한다.
export function OperationsDashboardPermissionRoute({ children }) {
  if (!hasValidToken()) return <Navigate to="/login" replace />;
  const level = getCurrentUserLevel();
  if (isSuperAdminUser() || ['lv2', 'lv3', 'lv4'].includes(level)) return children;
  return <Navigate to="/unauthorized" replace />;
}
